import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, CalendarCheck, MapPin, DollarSign, CheckCircle2, XCircle, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (c) => `$${((c || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

export default function VendorBookingRespondPage() {
  const { token } = useParams();
  const [b, setB] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/service-bookings/respond/${token}`)
      .then(r => setB(r.data))
      .catch(e => setError(e.response?.data?.detail || 'This link is not valid'));
  }, [token]);

  const respond = async (action) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/service-bookings/respond/${token}`, { action, message });
      setResult(r.data);
      toast.success(action === 'accept' ? 'Booking accepted — the gig is yours once the deposit lands' : 'Request declined');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not submit'); }
    finally { setBusy(false); }
  };

  if (error) return <div className="min-h-screen bg-background pt-32 text-center px-4"><XCircle className="h-10 w-10 mx-auto text-rose-400 mb-3" /><p className="font-semibold">{error}</p></div>;
  if (!b) return <div className="min-h-screen bg-background pt-32 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>;

  const settled = result || b.status !== 'requested';
  const finalStatus = result?.status || b.status;

  return (
    <div className="min-h-screen bg-background pt-24 pb-16 px-4" data-testid="vendor-respond-page">
      <div className="max-w-lg mx-auto space-y-4">
        <div className="text-center">
          <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-fuchsia-400 mb-1">Eventism · Booking Request</p>
          <h1 className="text-2xl font-extrabold">{b.service_name}</h1>
          <p className="text-sm text-muted-foreground">for {b.event?.name}</p>
        </div>

        <Card className="border-border"><CardContent className="py-4 space-y-2 text-sm">
          <p className="flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-fuchsia-400" />
            <strong>{b.event?.date}</strong>{b.event?.time ? ` · ${b.event.time}` : ''}</p>
          <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-fuchsia-400" />
            {b.event?.venue_name || (b.event?.city || '').replace('-', ' ')}</p>
          {(b.guest_count > 0 || b.hours > 0) && (
            <p className="text-muted-foreground">{b.guest_count > 0 ? `${b.guest_count} guests` : ''}{b.guest_count > 0 && b.hours > 0 ? ' · ' : ''}{b.hours > 0 ? `${b.hours} hours` : ''}</p>
          )}
          {(b.selected_options || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {b.selected_options.map((o, i) => (
                <span key={i} className="text-[11px] px-2 py-0.5 rounded-full border border-border">{o.group}: {o.choice}</span>
              ))}
            </div>
          )}
          {b.notes && <p className="text-xs text-muted-foreground border-t border-border pt-2 mt-2">"{b.notes}"</p>}
          <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/25 p-3 mt-2">
            <p className="flex items-center gap-2 font-bold text-emerald-400"><DollarSign className="h-4 w-4" /> Your payout: {usd(b.vendor_net_cents)}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Guaranteed by Eventism — paid after the event. Client pays Eventism directly; no invoicing or chasing.</p>
          </div>
          {!settled && (
            <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Lock className="h-3 w-3" /> Organizer contact details are shared once you accept.</p>
          )}
        </CardContent></Card>

        {settled ? (
          <Card className="border-border"><CardContent className="py-5 text-center space-y-2" data-testid="respond-result">
            {finalStatus === 'accepted' || finalStatus === 'locked' || finalStatus === 'paid' ? (
              <>
                <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-400" />
                <p className="font-bold">You accepted this booking</p>
                {(result?.organizer || b.organizer_email) && (
                  <p className="text-sm text-muted-foreground">Organizer: {(result?.organizer?.name || b.organizer_name) || ''} · {(result?.organizer?.email || b.organizer_email) || ''}</p>
                )}
                <p className="text-xs text-muted-foreground">The date locks when their deposit is paid — we'll email you the moment it lands.</p>
              </>
            ) : (
              <>
                <XCircle className="h-8 w-8 mx-auto text-rose-400" />
                <p className="font-bold capitalize">{finalStatus === 'declined' ? 'You declined this request' : `Request ${finalStatus}`}</p>
              </>
            )}
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            <Textarea placeholder="Optional message to the organizer…" rows={2} value={message}
              onChange={e => setMessage(e.target.value)} data-testid="respond-message" />
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10" disabled={busy}
                onClick={() => respond('decline')} data-testid="respond-decline">Decline</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy}
                onClick={() => respond('accept')} data-testid="respond-accept">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Accept booking'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
